import React, { useCallback, useEffect, useRef } from 'react';

const MIN_SCREEN_WIDTH = 80;
const MIN_SCREEN_HEIGHT = 24;
const DRAG_THRESHOLD_PX = 4;

const SignatureBox = ({ field, screenRect, selected, onSelect, onGeometryChange, boxRef }) => {
  const dragState = useRef(null);

  const label = field.full_name?.trim() || 'Nova assinatura';

  const startInteraction = useCallback(
    (mode, clientX, clientY) => {
      dragState.current = {
        mode,
        startX: clientX,
        startY: clientY,
        origin: { ...screenRect },
        dragging: mode === 'resize',
      };

      const onMove = (e) => {
        if (!dragState.current) return;
        const dx = e.clientX - dragState.current.startX;
        const dy = e.clientY - dragState.current.startY;

        if (!dragState.current.dragging) {
          if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
          dragState.current.dragging = true;
        }

        const origin = dragState.current.origin;

        if (dragState.current.mode === 'move') {
          onGeometryChange({
            left: origin.left + dx,
            top: origin.top + dy,
            width: origin.width,
            height: origin.height,
          });
        } else if (dragState.current.mode === 'resize') {
          onGeometryChange({
            left: origin.left,
            top: origin.top,
            width: Math.max(MIN_SCREEN_WIDTH, origin.width + dx),
            height: Math.max(MIN_SCREEN_HEIGHT, origin.height + dy),
          });
        }
      };

      const onUp = () => {
        const wasDragging = dragState.current?.dragging;
        dragState.current = null;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        if (!wasDragging) onSelect();
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [onGeometryChange, onSelect, screenRect]
  );

  useEffect(
    () => () => {
      dragState.current = null;
    },
    []
  );

  return (
    <div
      ref={boxRef}
      data-signature-box-id={field.signer_id}
      role="button"
      tabIndex={selected ? 0 : -1}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      onPointerDown={(e) => {
        if (e.target.closest('[data-resize-handle]')) return;
        e.stopPropagation();
        startInteraction('move', e.clientX, e.clientY);
      }}
      className={`absolute border-2 rounded-sm select-none touch-none ${
        selected ? 'border-primary ring-2 ring-primary/30' : 'border-sky-400'
      } ${field.isDraft && !field.isConfirmed ? 'border-dashed' : ''}`}
      style={{
        left: screenRect.left,
        top: screenRect.top,
        width: screenRect.width,
        height: screenRect.height,
        backgroundColor: 'rgb(240, 248, 255)',
        opacity: 0.92,
        cursor: 'move',
      }}
    >
      <div
        className="px-1 py-0.5 text-[10px] leading-tight font-medium text-sky-900 truncate pointer-events-none"
        title={label}
      >
        {label}
      </div>

      <div
        data-resize-handle
        className="absolute bottom-0 right-0 w-3 h-3 bg-primary cursor-se-resize rounded-tl-sm"
        onPointerDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
          startInteraction('resize', e.clientX, e.clientY);
        }}
      />
    </div>
  );
};

export default SignatureBox;
